import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { AppNumberInput } from '@pro-sto/ui';
import type { PaymentSchemeRateType, PaymentSchemeServiceRow } from '@/types/employee.types';
import { moneyValueToInputString } from '@/utils/money';
import './PaymentSchemeEditor.css';

// Пустое поле показываем как "0", а не пустую ячейку — так визуально понятно,
// что там уже стоит ставка, а не "поле ещё не тронуто" (это отличие видно
// только по индикатору "изменено вручную" у строк услуг). Это чисто
// отображение: наружу (в onBlur-коллбэках) null и 0 остаются разными
// значениями — см. комментарий у handleBlur ниже.
const inputValueOrZero = (value: string | null): string => moneyValueToInputString(value) || '0';

// AppNumberInput клампит и форматирует значение асинхронно (через onChange),
// поэтому на blur нельзя доверять локальному state — он ещё не обновлён из
// предыдущего рендера. Клампим значение из "сырой" строки сами.
const parseClamped = (raw: string, min?: number, max?: number): number | null => {
  if (raw.trim() === '') return null;
  const numeric = Number(raw.replace(',', '.'));
  if (Number.isNaN(numeric)) return null;
  let value = numeric;
  if (min !== undefined && value < min) value = min;
  if (max !== undefined && value > max) value = max;
  return value;
};

interface SegmentedControlProps {
  value: PaymentSchemeRateType;
  onChange: (value: PaymentSchemeRateType) => void;
  disabled?: boolean;
}

const RATE_TYPE_OPTIONS: { value: PaymentSchemeRateType; label: string }[] = [
  { value: 'fixed', label: 'Фикс' },
  { value: 'percent', label: 'Процент' },
];

const RateTypeSegmentedControl: React.FC<SegmentedControlProps> = ({ value, onChange, disabled }) => (
  <div className="payment-scheme-editor__segmented" role="tablist">
    {RATE_TYPE_OPTIONS.map((option) => (
      <button
        key={option.value}
        type="button"
        role="tab"
        aria-selected={value === option.value}
        disabled={disabled}
        className={classNames('payment-scheme-editor__segmented-tab', {
          'payment-scheme-editor__segmented-tab_active': value === option.value,
        })}
        onClick={() => onChange(option.value)}
      >
        {option.label}
      </button>
    ))}
  </div>
);

interface ServiceRowProps {
  row: PaymentSchemeServiceRow;
  disabled?: boolean;
  isSaving?: boolean;
  onRateTypeChange: (rateType: PaymentSchemeRateType, value: number) => void;
  onValueBlur: (rateType: PaymentSchemeRateType, value: number | null) => void;
}

const ServiceRow: React.FC<ServiceRowProps> = ({ row, disabled, isSaving, onRateTypeChange, onValueBlur }) => {
  const [localValue, setLocalValue] = useState(inputValueOrZero(row.value));
  const [rateType, setRateType] = useState<PaymentSchemeRateType>(row.rate_type ?? 'fixed');
  // true, если пользователь реально печатал в это поле в текущей сессии
  // редактирования (сбрасывается только когда снизу приходит новый row —
  // т.е. сервер подтвердил сохранение). Отличаем "поле не трогали" от
  // "поле трогали и очистили" — оба визуально выглядят как "0".
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setLocalValue(inputValueOrZero(row.value));
    setRateType(row.rate_type ?? 'fixed');
    setTouched(false);
  }, [row.value, row.rate_type]);

  // Есть ли у строки значение, которое имеет смысл конвертировать при смене
  // типа ставки: либо уже сохранённое на сервере (row.value), либо введённое
  // в этой сессии, но ещё не отправленное (touched). Проверять нужно именно
  // так, а не только row.value — иначе смена типа сразу после ввода (blur от
  // клика по сегменту срабатывает раньше onClick) не долетит до черновика.
  const hasValue = touched || row.value !== null;

  const handleChange = (value: string) => {
    setTouched(true);
    setLocalValue(value);
  };

  const handleRateTypeChange = (nextRateType: PaymentSchemeRateType) => {
    setRateType(nextRateType);
    if (!hasValue) return; // пустая ячейка — менять тип только визуально, нечего конвертировать
    const max = nextRateType === 'percent' ? 100 : undefined;
    // Значение введено в единицах старого типа ставки (₽ или %) — при
    // переключении на "Процент" клампим сразу и в локальном отображении, и
    // в том, что уйдёт наружу, — иначе поле показывает "500%", а сохранится 100.
    const parsed = parseClamped(localValue, 0, max) ?? 0;
    setLocalValue(String(parsed));
    setTouched(true);
    onRateTypeChange(nextRateType, parsed);
  };

  const handleBlur = () => {
    if (!touched) return; // поле не трогали — коммитить нечего и незачем
    const max = rateType === 'percent' ? 100 : undefined;
    const parsed = parseClamped(localValue, 0, max);
    setLocalValue(parsed === null ? '0' : String(parsed));
    onValueBlur(rateType, parsed);
  };

  return (
    <div className="payment-scheme-editor__row">
      <span className="payment-scheme-editor__row-name">
        {row.name}
        {row.is_overridden && (
          <span className="payment-scheme-editor__override-dot" title="Изменено вручную, отличается от шаблона" />
        )}
      </span>
      <div className="payment-scheme-editor__row-controls">
        <AppNumberInput
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          integerOnly={false}
          min={0}
          max={rateType === 'percent' ? 100 : undefined}
          suffix={rateType === 'percent' ? '%' : '₽'}
          disabled={disabled || isSaving}
          className="payment-scheme-editor__value-input"
          data-testid={`payment-scheme-service-value-${row.service_uuid}`}
        />
        <RateTypeSegmentedControl
          value={rateType}
          onChange={handleRateTypeChange}
          disabled={disabled || isSaving}
        />
      </div>
    </div>
  );
};

export interface PaymentSchemeEditorProps {
  fixPerShift: string | null;
  hourlyRate: string | null;
  services: PaymentSchemeServiceRow[];
  onFixPerShiftBlur: (value: number | null) => void;
  onHourlyRateBlur: (value: number | null) => void;
  onServiceRateTypeChange: (serviceUuid: string, rateType: PaymentSchemeRateType, value: number) => void;
  onServiceValueBlur: (serviceUuid: string, rateType: PaymentSchemeRateType, value: number | null) => void;
  savingServiceUuid?: string | null;
  isSavingFixHourly?: boolean;
  disabled?: boolean;
}

export const PaymentSchemeEditor: React.FC<PaymentSchemeEditorProps> = ({
  fixPerShift,
  hourlyRate,
  services,
  onFixPerShiftBlur,
  onHourlyRateBlur,
  onServiceRateTypeChange,
  onServiceValueBlur,
  savingServiceUuid,
  isSavingFixHourly,
  disabled,
}) => {
  const [localFix, setLocalFix] = useState(inputValueOrZero(fixPerShift));
  const [localHourly, setLocalHourly] = useState(inputValueOrZero(hourlyRate));
  const [fixTouched, setFixTouched] = useState(false);
  const [hourlyTouched, setHourlyTouched] = useState(false);

  useEffect(() => {
    setLocalFix(inputValueOrZero(fixPerShift));
    setFixTouched(false);
  }, [fixPerShift]);

  useEffect(() => {
    setLocalHourly(inputValueOrZero(hourlyRate));
    setHourlyTouched(false);
  }, [hourlyRate]);

  const handleFixBlur = () => {
    if (!fixTouched) return;
    const parsed = parseClamped(localFix, 0);
    setLocalFix(parsed === null ? '0' : String(parsed));
    onFixPerShiftBlur(parsed);
  };

  const handleHourlyBlur = () => {
    if (!hourlyTouched) return;
    const parsed = parseClamped(localHourly, 0);
    setLocalHourly(parsed === null ? '0' : String(parsed));
    onHourlyRateBlur(parsed);
  };

  return (
    <div className="payment-scheme-editor">
      <h2 className="payment-scheme-editor__title">Схема оплаты</h2>
      <p className="payment-scheme-editor__hint">
        Варианты оплаты суммируются, если в нескольких полях заполнено более 0₽.
      </p>

      <div className="payment-scheme-editor__fields">
        <AppNumberInput
          label="Фикс за выход"
          placeholder="0₽"
          value={localFix}
          onChange={(value) => {
            setFixTouched(true);
            setLocalFix(value);
          }}
          onBlur={handleFixBlur}
          suffix="₽"
          min={0}
          disabled={disabled || isSavingFixHourly}
          data-testid="payment-scheme-fix-per-shift"
        />
        <AppNumberInput
          label="Ставка за час"
          placeholder="0₽"
          value={localHourly}
          onChange={(value) => {
            setHourlyTouched(true);
            setLocalHourly(value);
          }}
          onBlur={handleHourlyBlur}
          suffix="₽"
          min={0}
          disabled={disabled || isSavingFixHourly}
          data-testid="payment-scheme-hourly-rate"
        />
      </div>

      <h3 className="payment-scheme-editor__services-title">За услуги</h3>
      <div className="payment-scheme-editor__services">
        {services.length === 0 && (
          <p className="payment-scheme-editor__empty">Нет доступных услуг</p>
        )}
        {services.map((row) => (
          <ServiceRow
            key={row.service_uuid}
            row={row}
            disabled={disabled}
            isSaving={savingServiceUuid === row.service_uuid}
            onRateTypeChange={(rateType, value) => onServiceRateTypeChange(row.service_uuid, rateType, value)}
            onValueBlur={(rateType, value) => onServiceValueBlur(row.service_uuid, rateType, value)}
          />
        ))}
      </div>
    </div>
  );
};

export default PaymentSchemeEditor;
